import { ActionContext, MutationTree, ActionTree, GetterTree } from 'vuex'
import { LinehaulRoadListItem, LinehaulRoad, LinehaulRoadFilter } from '../webApi/DataModels'
import webApi from '../webApi'
import { each, extend, find, orderBy } from 'lodash'
import moment from 'moment'

interface ILinehaulRoadListFilter {
  From_Date: string;
  To_Date: string;
}

function resolveValues (id: number, values: any[], idFieldName: string, valueFieldName: string, resolvedLabel: string): String {
  var resolvedValues = []
  var filterObject = {}
  filterObject[idFieldName] = id
  var value = find(values, filterObject)
  if (value && value[valueFieldName]) {
    resolvedValues.push(value[valueFieldName])
  }
  if (resolvedValues.length > 3) {
    return resolvedValues.length + ' ' + resolvedLabel
  } else if (resolvedValues.length === 0) {
    return ''
  } else {
    return resolvedValues.join(', ')
  }
}

class LinehaulRoadState {
  public linehaulRoadList: LinehaulRoadListItem[] = []
  public filteredLinehaulRoadList: LinehaulRoadListItem[] = []
  public loadedLinehaulRoad: LinehaulRoad = null
  public linehaulRoadResult: any = null
  public currentEditLinehaulRoad: LinehaulRoad = null
  public linehaulRoadFilter: LinehaulRoadFilter = null
  public linehaulRoads: LinehaulRoad[] = []
  public linehaulRoadOptions: any[] = []
}

const mutations = <MutationTree<LinehaulRoadState>> {
  // sets the linehaulRoad list and resolves the linehaul roads
  setLinehaulRoadList (state: LinehaulRoadState, { list, linehaulRoads }: { list: LinehaulRoadListItem[], linehaulRoads: LinehaulRoad[] }) {
    each(list, (linehaulRoadItem: LinehaulRoad, index: number) => {
      extend(linehaulRoadItem, {
        Linehaul_Drive_Name: resolveValues(linehaulRoadItem.Linehaul_Drive_ID, linehaulRoads, 'Linehaul_Drive_ID', 'Linehaul_Drive_Name', 'linehaulRoads')
      })
    })

    state.linehaulRoadList = list
    state.filteredLinehaulRoadList = list
  },
  setLinehaulRoadOptions (state: LinehaulRoadState, linehaulRoads: LinehaulRoad[]) {
    state.linehaulRoads = linehaulRoads
    state.linehaulRoadOptions = linehaulRoads.map((linehaulRoad: LinehaulRoad) => {
      return {
        value: linehaulRoad.Linehaul_Drive_ID,
        label: linehaulRoad.Linehaul_Drive_Name,
        selected: false
      }
    })
    window.sessionStorage.setItem('Linehaul_Road_List', JSON.stringify(state.linehaulRoads))
  },
  setCurrentEditLinehaulRoad (state: LinehaulRoadState, linehaulRoad: LinehaulRoad): void {
    state.currentEditLinehaulRoad = linehaulRoad
  },
  setLoadedLinehaulRoad (state: LinehaulRoadState, linehaulRoad: LinehaulRoad): void {
    state.loadedLinehaulRoad = linehaulRoad
  },
  filteredLinehaulRoadList (state:LinehaulRoadState, { sortFields, sortDirections }) {
    state.filteredLinehaulRoadList = orderBy(state.filteredLinehaulRoadList, sortFields, sortDirections)
    state.linehaulRoadList = orderBy(state.linehaulRoadList, sortFields, sortDirections)
  },
  setLinehaulRoadFilter (state: LinehaulRoadState, linehaulRoadFilter: LinehaulRoadFilter) {
    state.linehaulRoadFilter = linehaulRoadFilter
  },
  reset (state:LinehaulRoadState) {
    state.linehaulRoadList = []
    state.filteredLinehaulRoadList = []
    state.loadedLinehaulRoad = null
    state.linehaulRoadResult = null
    state.currentEditLinehaulRoad = null
    state.linehaulRoadFilter = null
    state.linehaulRoads = []
    state.linehaulRoadOptions = []
  },
  resetLinehaulRoad (state: LinehaulRoadState): void {
    state.loadedLinehaulRoad = null
  }
}

const actions = <ActionTree<LinehaulRoadState, any>>{
  reset (store: ActionContext<LinehaulRoadState, any>):void {
    store.commit('reset')
  },
  loadLinehaulRoadFilter (store: ActionContext<LinehaulRoadState, any>, linehaulRoadFilter: LinehaulRoadFilter) {
    store.commit('setLinehaulRoadFilter', linehaulRoadFilter)
  },
  // loads the linehaulRoad list
  loadLinehaulRoadList (store: ActionContext<LinehaulRoadState, any>, linehaulRoadListFilter: ILinehaulRoadListFilter): Promise<any> {
    return webApi.linehaulRoad.get_LinehaulRoad_List(linehaulRoadListFilter).then(
      (response) => {
        store.commit('setLinehaulRoadList', {
          list: response.data,
          linehaulRoads: store.rootState.linehaulRoad.linehaulRoads
        })
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // loads a linehaulRoad
  loadLinehaulRoad (store: ActionContext<LinehaulRoadState, any>, linehaulRoadID: number): Promise<any> {
    store.commit('resetLinehaulRoad')
    return webApi.linehaulRoad.get_LinehaulRoad_By_ID(linehaulRoadID).then(
      (response: any) => {
        var result: LinehaulRoad = response.data as LinehaulRoad
        store.commit('setLoadedLinehaulRoad', result)
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  loadLinehaulRoadOptions (store: ActionContext<LinehaulRoadState, any>):Promise<any> {
    if (window.sessionStorage.getItem('Linehaul_Road_List') != null) {
      // the list has already been stored in session
      store.commit('setLinehaulRoadOptions', JSON.parse(window.sessionStorage.getItem('Linehaul_Road_List')))
      return Promise.resolve('success')
    } else {
      // the list has not been stored in session yet, load from server
      return webApi.linehaulRoad.get_LinehaulRoad_Options().then(
        (response) => {
          // Store the response
          store.commit('setLinehaulRoadOptions', response.data)
          return Promise.resolve('success')
        }).catch((response) => {
        window.bus.$emit('openErrorModal', 'error loading countries: ' + response.ExceptionMessage)
        return Promise.reject(response)
      })
    }
  },
  updateLinehaulRoad (store: ActionContext<LinehaulRoadState, any>, linehaulRoad: any): Promise<any> {
    return webApi.linehaulRoad.update_LinehaulRoad(linehaulRoad).then(
      (response) => {
        return Promise.resolve(response)
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // saves the linehaulRoad to the database
  saveLinehaulRoad (store: ActionContext<LinehaulRoadState, any>, linehaulRoad: any): Promise<any> {
    return webApi.linehaulRoad.save_LinehaulRoad(linehaulRoad).then(
      (response) => {
        var filter = {
          fromDate: moment(linehaulRoad.Linehaul_Drive_Detail_Date).format(),
          toDate: moment(linehaulRoad.Linehaul_Drive_Detail_Date).format()
        }
        store.commit('setLinehaulRoadFilter', filter)
        return Promise.resolve(response)
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  sortList ({ commit }, { sortFields, sortDirections }): void {
    commit('filteredLinehaulRoadList', { sortFields, sortDirections })
  }
}

const getters = <GetterTree<LinehaulRoadState, any>>{
  linehaulRoadList: (state): LinehaulRoadListItem[] => {
    return state.filteredLinehaulRoadList
  },
  loadedLinehaulRoad: (state): LinehaulRoad => {
    return state.loadedLinehaulRoad
  },
  linehaulRoadResult: (state): any => {
    return state.linehaulRoadResult
  },
  linehaulRoadFilter: (state: LinehaulRoadState): LinehaulRoadFilter => {
    return state.linehaulRoadFilter
  },
  linehaulRoad: (state) => {
    return state.linehaulRoads
  },
  linehaulRoadOptions: (state) => {
    return state.linehaulRoadOptions
  }
}

export default ({
  namespaced: true,
  state: new LinehaulRoadState(),
  mutations,
  actions,
  getters
})
